import React, { useState, useMemo } from 'react';
import { useAvailability } from '../hooks/useAvailability';
import { useRequests, Request } from '../hooks/useRequests';
import Calendar from '../components/Calendar';

const DAYS_NL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

export default function SchoolCalendarPage() {
  const { availability, isConnected, isLoading } = useAvailability();
  const { requests, createRequest, deleteRequest } = useRequests();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  
  // Multi-select state for requests
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [requestNotes, setRequestNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const selectedDateFormatted = useMemo(() => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate);
    return `${DAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]} ${d.getFullYear()}`;
  }, [selectedDate]);

  const selectedData = selectedDate ? availability[selectedDate] : null;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${MONTHS_NL[d.getMonth()].substring(0, 3)}`;
  };

  const toggleMultiSelectMode = () => {
    if (multiSelectMode) {
      setMultiSelectMode(false);
      setSelectedDates([]);
      setRequestNotes('');
    } else {
      setMultiSelectMode(true);
      setSelectedDate(null);
    }
    setMessage('');
  };

  const handleMultiSelect = (dates: string[]) => {
    // Only allow selecting available dates
    const availableDates = dates.filter(d => {
      const avail = availability[d];
      return avail && (avail.status === 'available' || avail.status === 'partial');
    });
    setSelectedDates(availableDates);
  };

  const handleSubmitRequest = async () => {
    if (selectedDates.length === 0) return;
    
    setIsSubmitting(true);
    setMessage('');
    
    try {
      await createRequest(selectedDates, requestNotes);
      setMessage('✅ Aanvraag verstuurd!');
      setSelectedDates([]);
      setRequestNotes('');
      setMultiSelectMode(false);
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('Weet je zeker dat je deze aanvraag wilt annuleren?')) return;
    
    try {
      await deleteRequest(requestId);
    } catch (error: any) {
      alert(`Fout: ${error.message}`);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#e65100', icon: '⏳', text: 'In behandeling' };
      case 'accepted':
        return { bg: '#e8f5e9', color: '#2e7d32', icon: '✅', text: 'Geaccepteerd' };
      case 'declined':
        return { bg: '#ffebee', color: '#c62828', icon: '❌', text: 'Afgewezen' };
      default:
        return { bg: '#f5f5f5', color: '#666', icon: '❓', text: status };
    }
  };

  if (isLoading) {
    return (
      <div className="container" style={{ textAlign: 'center', marginTop: '50px' }}>
        Beschikbaarheid laden...
      </div>
    );
  }

  return (
    <div className="container">
      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">👤</div>
          <div className="profile-info">
            <h2>Beschikbaarheid Agenda</h2>
            <p>Bekijk wanneer er beschikbaarheid is voor invalwerk</p>
          </div>
          <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginLeft: 'auto' }}>
            <button
              className={`btn ${multiSelectMode ? 'btn-secondary' : 'btn-outline'}`}
              onClick={toggleMultiSelectMode}
              style={{ padding: '8px 16px', fontSize: '0.9rem', marginTop: 0 }}
            >
              {multiSelectMode ? '✓ Aanvraag indienen' : '📅 Dagen aanvragen'}
            </button>
            <div className={`realtime-indicator ${isConnected ? '' : 'disconnected'}`}>
              <div className="realtime-dot"></div>
              {isConnected ? 'Live updates' : 'Offline'}
            </div>
          </div>
        </div>
      </div>

      {multiSelectMode && (
        <div style={{ 
          background: '#e8f5e9', 
          padding: '12px 16px', 
          borderRadius: 8, 
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            <strong>Aanvraag modus:</strong> Klik op beschikbare dagen om ze te selecteren voor een aanvraag. 
            {selectedDates.length > 0 && ` (${selectedDates.length} dagen geselecteerd)`}
          </span>
          {selectedDates.length > 0 && (
            <button
              onClick={() => setSelectedDates([])}
              style={{ background: 'transparent', border: 'none', color: '#2e7d32', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Selectie wissen
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
        <div>
          <Calendar
            availability={availability}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            multiSelect={multiSelectMode}
            selectedDates={selectedDates}
            onMultiSelect={handleMultiSelect}
          />
          
          <div className="legend">
            <div className="legend-item">
              <div className="legend-dot available"></div>
              Beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot partial"></div>
              Gedeeltelijk beschikbaar
            </div>
          </div>

          <div style={{ marginTop: 20, padding: 15, background: '#e3f2fd', borderRadius: 8 }}>
            <strong>💡 Tip:</strong> Klik op "Dagen aanvragen" om meerdere dagen te selecteren en een aanvraag in te dienen.
          </div>
        </div>

        {/* Multi-select panel for requests */}
        {multiSelectMode && (
          <div className="day-detail">
            <h3>
              {selectedDates.length === 0 
                ? 'Selecteer beschikbare dagen' 
                : `${selectedDates.length} dag${selectedDates.length === 1 ? '' : 'en'} geselecteerd`}
            </h3>

            {selectedDates.length > 0 && (
              <>
                <div style={{ marginTop: 15, marginBottom: 20 }}>
                  <label style={{ fontWeight: 500, marginBottom: 10, display: 'block', color: '#666' }}>
                    Geselecteerde dagen:
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectedDates.sort().map(date => (
                      <span 
                        key={date} 
                        style={{
                          background: '#e8f5e9',
                          padding: '6px 12px',
                          borderRadius: 16,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        {formatDate(date)}
                        <button
                          onClick={() => setSelectedDates(selectedDates.filter(d => d !== date))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <textarea
                  className="notes-input"
                  placeholder="Notities bij je aanvraag (optioneel, bijv. welke klas/groep)"
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  style={{ marginBottom: 15 }}
                />

                <button
                  className="btn btn-primary"
                  onClick={handleSubmitRequest}
                  disabled={isSubmitting}
                  style={{ width: '100%' }}
                >
                  {isSubmitting ? 'Versturen...' : `Aanvraag versturen (${selectedDates.length} dag${selectedDates.length === 1 ? '' : 'en'})`}
                </button>

                {message && (
                  <div style={{ marginTop: 15, textAlign: 'center' }}>{message}</div>
                )}
              </>
            )}

            {selectedDates.length === 0 && (
              <p style={{ color: '#666', marginTop: 20 }}>
                Klik op groene (beschikbare) dagen in de kalender om ze te selecteren. 
                Je kunt dan een aanvraag indienen voor invalwerk.
              </p>
            )}
          </div>
        )}

        {/* Single date view panel */}
        {!multiSelectMode && (
          <div className="day-detail">
            {selectedDate ? (
              <>
                <h3>{selectedDateFormatted}</h3>
                
                {selectedData ? (
                  <div style={{ marginTop: 20 }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      background: selectedData.status === 'available' ? '#e8f5e9' : '#fff3e0',
                      borderRadius: 8,
                      marginBottom: 20
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>
                        {selectedData.status === 'available' ? '✅' : '🕐'}
                      </span>
                      <span style={{ fontWeight: 500 }}>
                        {selectedData.status === 'available' ? 'Beschikbaar' : 'Gedeeltelijk beschikbaar'}
                      </span>
                    </div>

                  {selectedData.timeSlots && selectedData.timeSlots.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ fontWeight: 500, marginBottom: 10, display: 'block', color: '#666' }}>
                        Beschikbare tijden:
                      </label>
                      {selectedData.timeSlots.map((slot, index) => (
                        <div key={index} style={{
                          padding: '12px 16px',
                          background: '#f5f5f5',
                          borderRadius: 8,
                          marginBottom: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>🕒</span>
                          <span style={{ fontWeight: 500 }}>{slot.start}</span>
                          <span style={{ color: '#999' }}>tot</span>
                          <span style={{ fontWeight: 500 }}>{slot.end}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedData.notes && (
                    <div style={{
                      padding: 15,
                      background: '#fafafa',
                      borderRadius: 8,
                      borderLeft: '4px solid #4A90D9'
                    }}>
                      <label style={{ fontWeight: 500, marginBottom: 8, display: 'block', color: '#666' }}>
                        Notities:
                      </label>
                      <p style={{ margin: 0 }}>{selectedData.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  marginTop: 30, 
                  textAlign: 'center', 
                  color: '#999',
                  padding: 40 
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: 15 }}>📅</div>
                  <p>Geen beschikbaarheid op deze datum</p>
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: '#666',
              textAlign: 'center',
              padding: 40
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 15 }}>👆</div>
              <p>Selecteer een datum in de kalender om de beschikbaarheid te bekijken</p>
            </div>
          )}
          </div>
        )}
      </div>

      {/* My Requests Section */}
      {requests.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <h3 style={{ marginBottom: 15 }}>Mijn Aanvragen</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {requests.map((request) => {
              const badge = getStatusBadge(request.status);
              return (
                <div 
                  key={request.id}
                  style={{
                    background: 'white',
                    borderRadius: 12,
                    padding: 20,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span 
                        style={{
                          background: badge.bg,
                          color: badge.color,
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: '0.85rem',
                          fontWeight: 500
                        }}
                      >
                        {badge.icon} {badge.text}
                      </span>
                      <span style={{ color: '#999', fontSize: '0.85rem' }}>
                        {new Date(request.createdAt).toLocaleDateString('nl-NL')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: request.notes ? 10 : 0 }}>
                      {request.dates.sort().map(date => (
                        <span 
                          key={date}
                          style={{
                            background: '#f5f5f5',
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: '0.85rem'
                          }}
                        >
                          {formatDate(date)}
                        </span>
                      ))}
                    </div>
                    {request.notes && (
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        📝 {request.notes}
                      </p>
                    )}
                    {request.adminNotes && (
                      <p style={{ margin: '8px 0 0 0', color: '#4A90D9', fontSize: '0.9rem' }}>
                        💬 {request.adminNotes}
                      </p>
                    )}
                  </div>
                  {request.status === 'pending' && (
                    <button
                      onClick={() => handleDeleteRequest(request.id)}
                      style={{
                        background: '#ffebee',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        color: '#c62828'
                      }}
                    >
                      Annuleren
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
